import React, { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import notify from '../../utilities/notify'
import { useParams, useHistory, generatePath, useLocation } from 'react-router-dom'
import { ObjectParam, useQueryParams } from 'use-query-params'
import QUERY_KEYS from '../../graphql/queryKeys'
import { DELETE_TICKETS_CONFIRMATION_MESSAGE, TICKET_BILLING_STATUSES } from '../../utilities/constants'
import styles from './index.module.scss'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import PageTitle from '../../components/page-title'
import { Spinner } from '../shared/Spinner'
import FormExplanation from '../shared/FormExplanation'
import EditTicketForm from '../../components/edit-ticket-form'
import useQuery from '../../hooks/useQuery'
import useMutation from '../../hooks/useMutation'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import { formatFormValuesForSubmit } from '../../utilities/randomUtilities'
import updateTicketByIdMutation from '../../graphql/mutations/updateTicketById'
import updateRecurringTicketsMutation from '../../graphql/mutations/updateRecurringTickets'
import { ROUTES } from '../../routes'
import editTicketPageQuery from '../../graphql/queries/pages/editTicketPage'
import weightUnitsQuery from '../../graphql/queries/weightUnits'
import feeTypesQuery from '../../graphql/queries/feeTypes'
import usersQuery from '../../graphql/queries/users'
import recurringTicketQuery from '../../graphql/queries/recurringTicket'
import ticketSearchQuery from '../../graphql/queries/ticketsSearch'
import RecurringTicketsTable from '../../components/recurring-services/RecurringTicketsTable'
import cn from 'classnames'

const pageSize = 5

export default function EditTicketPage () {
  const [ticketTypeBillingStatusSelected, setTicketTypeBillingStatusSelected] = useState(null)
  const [resourceTypeBillingStatusSelected, setResourceTypeBillingStatusSelected] = useState(null)
  const history = useHistory()
  const { id } = useParams()
  const location = useLocation()
  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location.search])
  const { user, hauler } = useSelector(({ user }) => ({ user: user.user, hauler: user.hauler }))
  const serviceFrequency = queryParams.get('serviceFrequency')
  const isRecurring = hauler.isRecurringServiceEnabled && serviceFrequency === 'RECURRING'
  const [ticketsSearchQueryParams, setTicketsSearchQueryParams] = useQueryParams({
    sort: ObjectParam,
    pager: ObjectParam
  })
  const [recurringTicketsFakeCount, setRecurringTicketsFakeCount] = useState(null)

  function filteredBillingStatusList () {
    if (!isRecurring && pageData?.ticket.invoice?.id !== undefined) {
      return { INVOICED: 'INVOICED', PAID: 'PAID' }
    }
    return TICKET_BILLING_STATUSES
  }

  const { data: pageData, isFetching: isFetchingPageData } = useQuery(
    [QUERY_KEYS.editTicketPage, { haulerId: user.haulerId, ticketId: id }],
    editTicketPageQuery,
    {
      enabled: !isRecurring,
      onSuccess (data) {
        const ticketTypeDefaultBillingStatus = data.ticket.ticketType?.defaultBillingStatus
        const resourceTypeDefaultBillingStatus = data.ticket.resourceType?.defaultBillingStatus
        function handleDefaultOptions (defaultValue) {
          const defaultInList = Object.keys(filteredBillingStatusList()).includes(defaultValue)
          if (!defaultInList) {
            return null
          }
          return defaultValue
        }
        setTicketTypeBillingStatusSelected(handleDefaultOptions(ticketTypeDefaultBillingStatus))
        setResourceTypeBillingStatusSelected(handleDefaultOptions(resourceTypeDefaultBillingStatus))
      },
      onError (error) {
        captureErrorAndNotify(error, 'Error fetching data')
      }
    }
  )

  const { data: recurringTicket, isFetching: isFetchingRecurringTicket } = useQuery(
    [QUERY_KEYS.recurringTicket, { haulerId: user.haulerId, ticketId: id }],
    recurringTicketQuery,
    {
      enabled: isRecurring,
      onSuccess (data) {
        const ticketTypeDefaultBillingStatus = data?.recurringTicket?.ticketType?.defaultBillingStatus
        const resourceTypeDefaultBillingStatus = data?.recurringTicket?.resourceType?.defaultBillingStatus
        function handleDefaultOptions (defaultValue) {
          const defaultInList = Object.keys(filteredBillingStatusList()).includes(defaultValue)
          if (!defaultInList) {
            return null
          }
          return defaultValue
        }
        setTicketTypeBillingStatusSelected(handleDefaultOptions(ticketTypeDefaultBillingStatus))
        setResourceTypeBillingStatusSelected(handleDefaultOptions(resourceTypeDefaultBillingStatus))
      },
      onError (error) {
        captureErrorAndNotify(error, 'Error fetching recurring ticket data')
      }
    }
  )

  const { data: recurringTickets, isFetching: isFetchingRecurringTickets } = useQuery([
    QUERY_KEYS.ticketsSearch,
    user.haulerId,
    pageSize,
    ticketsSearchQueryParams.sort,
    { recurringTicketId: id },
    ticketsSearchQueryParams.pager
  ],
  ticketSearchQuery,
  {
    enabled: Boolean(ticketsSearchQueryParams.sort) && id && isRecurring,
    onError () {
      notify('error', 'Error fetching recurring tickets')
    }
  })

  function onPageRequest (direction, cursor) {
    setTicketsSearchQueryParams({ pager: { cursor, direction, pageSize } })

    if (!recurringTicketsFakeCount) return
    if (direction === 'before') {
      setRecurringTicketsFakeCount(prev => {
        const newStart = Math.max(1, prev.start - pageSize)
        const newEnd = newStart + pageSize - 1

        return { start: newStart, end: newEnd }
      })
    }
    if (direction === 'after') {
      setRecurringTicketsFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  useEffect(function setDefaultQueryParams () {
    const defaultQueryParams = {}
    if (!ticketsSearchQueryParams.sort) {
      defaultQueryParams.sort = { column: 'date', direction: 'desc' }
    }
    setTicketsSearchQueryParams(defaultQueryParams, 'replaceIn')
  }, [ticketsSearchQueryParams, setTicketsSearchQueryParams])

  useEffect(function toggleFakePagination () {
    if (!recurringTickets || isFetchingRecurringTickets || recurringTicketsFakeCount !== null) return
    setRecurringTicketsFakeCount({ start: 1, end: Math.min(pageSize, recurringTickets?.ticketsSearch?.totalCount) })
  }, [recurringTickets, isFetchingRecurringTickets, recurringTicketsFakeCount, setRecurringTicketsFakeCount])

  const { data: feeTypesData } = useQuery(
    [QUERY_KEYS.feeTypes, 'ENABLED'],
    feeTypesQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Error fetching fee types')
      },
      placeholderData: { feeTypes: {} }
    }
  )

  const { data: { users } = {} } = useQuery([
    QUERY_KEYS.users,
    user.haulerId,
    true,
    null,
    null,
    hauler.showDriversOnly ? 'hauler_driver' : null
  ],
  usersQuery,
  {
    onError (error) {
      captureErrorAndNotify(error, 'Unable to retrieve user list')
    },
    placeholderData: { users: { nodes: [] } }
  })

  const { data: weightData, isFetching: isWeightUnitsFetching } = useQuery(
    [QUERY_KEYS.weightUnits, hauler?.id],
    weightUnitsQuery
  )

  const { mutate: updateTicket, isLoading: isUpdatingTicket } = useMutation(updateTicketByIdMutation, {
    onSuccess () {
      notify('success', 'Ticket updated successfully!')
      history.push(generatePath(ROUTES.ticket, { id }))
    },
    onError (error) {
      captureErrorAndNotify(error, 'Unable to update ticket')
    }
  })

  const { mutate: updateRecurringTickets, isLoading: isUpdatingRecurringTicket } = useMutation(updateRecurringTicketsMutation, {
    onSuccess () {
      notify('success', 'Recurring Tickets update started!')
      history.push(generatePath(ROUTES.job, { id: recurringTicket?.recurringTicket?.job?.id }))
    },
    onError (error) {
      captureErrorAndNotify(error, 'Unable to update recurring tickets')
    }
  })

  const isFetching = isUpdatingTicket || isFetchingPageData || isWeightUnitsFetching || isUpdatingRecurringTicket || isFetchingRecurringTicket

  function handleFormSubmit (values, form) {
    const initialValues = form.getState().initialValues
    const formattedValues = formatFormValuesForSubmit(initialValues, values)
    formattedValues.weight = parseFloat(formattedValues.weight)
    updateTicket({ ...formattedValues, id })
  }

  function handleRecurringTicketsFormSubmit (values, form) {
    const { ...formValues } = values
    const initialValues = form.getState().initialValues
    const formattedValues = formatFormValuesForSubmit(initialValues, formValues)
    formattedValues.serviceDailyFrequency = parseInt(formattedValues.serviceDailyFrequency)
    if (Array.isArray(formattedValues.serviceDate) && formattedValues.serviceDate.length) {
      formattedValues.serviceDate = formattedValues.serviceDate.map(v => parseInt(v))
    }
    const fieldsChanged = formValues.startDate > recurringTicket?.recurringTicket?.startDate ||
      formValues.serviceInterval !== recurringTicket?.recurringTicket?.serviceInterval ||
      formattedValues.serviceDate !== Number(recurringTicket?.recurringTicket?.serviceDate) ||
      formattedValues.serviceDailyFrequency !== Number(recurringTicket?.recurringTicket?.serviceDailyFrequency)
    const confirmAndUpdate = (message, onConfirm) => {
      const result = window.confirm(message)
      if (result) onConfirm()
    }

    const updateAction = () => {
      updateRecurringTickets({ ticketParams: transformRecurringFormValues(formattedValues, initialValues.ticketFees), id })
    }
    fieldsChanged ? confirmAndUpdate(DELETE_TICKETS_CONFIRMATION_MESSAGE, updateAction) : updateAction()
  }

  const transformRecurringFormValues = (values, initialTicketFees = []) => {
    const { ticketFees, feeTypeId, ...rest } = values

    const updatedTicketFees = []
    const newTicketFees = []

    const initialById = new Map(initialTicketFees.map(f => [f.id, f]))

    ticketFees.forEach(ticketFee => {
      const formatted = {
        amount: parseFloat(ticketFee.amount),
        ticketFeeId: ticketFee.id,
        description: ticketFee.description,
        quantity: Number(ticketFee.quantity),
        feeTypeId: ticketFee.feeType?.id
      }

      if (!ticketFee.id) {
        newTicketFees.push(formatted)
        return
      }

      const original = initialById.get(ticketFee.id)

      if (!original) return

      const isChanged =
        Number(original.amount) !== formatted.amount ||
        Number(original.quantity) !== formatted.quantity ||
        original.description !== formatted.description ||
        original.feeType?.id !== formatted.feeTypeId

      if (isChanged) {
        updatedTicketFees.push(formatted)
      }
    })

    return {
      ...rest,
      driverId: rest.driverId === 'null' ? null : rest.driverId,
      newTicketFees,
      updatedTicketFees
    }
  }

  const initialValues = useMemo(() => {
    if (isRecurring) {
      return {
        ticketTypeId: recurringTicket?.recurringTicket?.ticketType?.id,
        resourceTypeId: recurringTicket?.recurringTicket?.resourceType?.id,
        billingNotes: recurringTicket?.recurringTicket?.billingNotes,
        requestedStartTime: recurringTicket?.recurringTicket?.requestedStartTime,
        requestedEndTime: recurringTicket?.recurringTicket?.requestedEndTime,
        billingStatus: recurringTicket?.recurringTicket?.billingStatus,
        weightUnitsOfMeasure: recurringTicket?.recurringTicket?.weightUnitsOfMeasure
      }
    } else {
      return {
        ticketTypeId: pageData?.ticket?.ticketType?.id,
        resourceTypeId: pageData?.ticket?.resourceType?.id,
        weight: pageData?.ticket?.weight,
        billingNotes: pageData?.ticket?.billingNotes,
        requestedStartTime: pageData?.ticket?.requestedStartTime,
        requestedEndTime: pageData?.ticket?.requestedEndTime,
        billingStatus: pageData?.ticket?.billingStatus,
        weightTicketNumber: pageData?.ticket?.weightTicketNumber,
        weightUnitsOfMeasure: pageData?.ticket?.weightUnitsOfMeasure
      }
    }
  }, [pageData, recurringTicket, isRecurring])

  const recurringTicketValues = useMemo(() => {
    if (isRecurring) {
      return {
        startDate: recurringTicket?.recurringTicket?.startDate,
        endDate: recurringTicket?.recurringTicket?.endDate,
        serviceDay: recurringTicket?.recurringTicket?.serviceDay,
        serviceDate: recurringTicket?.recurringTicket?.serviceDate,
        serviceInterval: recurringTicket?.recurringTicket?.serviceInterval,
        serviceDailyFrequency: recurringTicket?.recurringTicket?.serviceDailyFrequency,
        driverId: recurringTicket?.recurringTicket?.driverId
      }
    } else {
      return {
        startDate: pageData?.ticket?.recurringTicket?.startDate,
        endDate: pageData?.ticket?.recurringTicket?.endDate,
        serviceDay: pageData?.ticket?.recurringTicket?.serviceDay,
        serviceDate: pageData?.ticket?.recurringTicket?.serviceDate,
        serviceInterval: pageData?.ticket?.recurringTicket?.serviceInterval,
        serviceDailyFrequency: pageData?.ticket?.recurringTicket?.serviceDailyFrequency,
        driverId: pageData?.ticket?.driverId
      }
    }
  }, [pageData, recurringTicket, isRecurring])

  const ticketFeeValues = useMemo(() => {
    if (isRecurring) {
      return {
        ticketFees: recurringTicket?.recurringTicket?.ticketFees?.map(tf => ({
          ...tf,
          amount: parseFloat(tf.amount).toFixed(2)
        })),
        deletedTicketFees: []
      }
    } else {
      return {
        ticketFees: pageData?.ticket?.ticketFees?.map(tf => ({
          ...tf,
          amount: parseFloat(tf.amount).toFixed(2)
        })),
        deletedTicketFees: []
      }
    }
  }, [pageData, recurringTicket, isRecurring])

  return (
    <NormalLayoutContainer showBackLink>
      <PageTitle>
        <div className={styles.pageTitle}>
          <div>{isRecurring ? 'Edit Recurring Service' : 'Edit a Ticket'}</div>
          <button
            type='button'
            className={cn('dis-btn dis-btn-blank dis-btn-lg', styles.cancelBtn)}
            onClick={() => history.goBack()}>
            Cancel
          </button>
        </div>
      </PageTitle>
      <div className='dis-form-page-container'>
        <div className='dis-form-container-left'>
          {isFetching
            ? <Spinner isFetching />
            : (
              <EditTicketForm
                isFetching={isFetching}
                metric={hauler.metric}
                onSubmit={isRecurring ? handleRecurringTicketsFormSubmit : handleFormSubmit}
                resourceTypes={isRecurring ? recurringTicket?.resourceTypes : pageData.resourceTypes}
                ticketTypes={isRecurring ? recurringTicket?.ticketTypes : pageData.ticketTypes}
                billingStatuses={filteredBillingStatusList()}
                ticketTypeBillingStatusSelected={ticketTypeBillingStatusSelected}
                setTicketTypeBillingStatusSelected={setTicketTypeBillingStatusSelected}
                weightOptions={weightData?.weightUnits}
                resourceTypeBillingStatusSelected={resourceTypeBillingStatusSelected}
                setResourceTypeBillingStatusSelected={setResourceTypeBillingStatusSelected}
                initialValues={initialValues}
                recurringTicketValues={recurringTicketValues}
                ticketFeeValues={ticketFeeValues}
                serviceFrequency={serviceFrequency}
                feeTypes={feeTypesData.feeTypes}
                users={users?.nodes}
              />
              )
          }
        </div>
        <div className='dis-form-container-right'>
          <FormExplanation
            messages={[
              'Tickets are the action that takes place on jobs. Here you can make changes to the following:',
              ' - Type of action needed',
              " - Asset type that's needed",
              ' - Enter weight of the ticket'
            ]}
          />
        </div>
      </div>
      {(!isFetching && isRecurring) &&
        <RecurringTicketsTable recurringTickets={recurringTickets?.ticketsSearch} onPageRequest={onPageRequest} recurringTicketsFakeCount={recurringTicketsFakeCount} recurringTicketId={id} />
      }
    </NormalLayoutContainer>
  )
}
